import { Link } from "react-router-dom";
import { ChevronRight, Phone, MessageCircle, Mail, MapPin, Clock, Navigation, CheckCircle2 } from "lucide-react";
import { company } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { Seo } from "@/components/Seo";
import { Reveal, Overline } from "@/components/Reveal";
import { QuoteForm } from "@/components/QuoteForm";
import { OperationalArea } from "@/components/OperationalArea";
import { CallButton, WhatsAppButton, LocationButton } from "@/components/ActionButtons";

export default function Contatti() {
  const { t, lang } = useLang();
  const c = t.contact;

  const details = [
    { icon: Phone, label: c.phone, value: company.phoneDisplay, href: company.phoneHref, testId: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: company.phoneDisplay, href: `https://wa.me/${company.whatsapp}`, testId: "contact-whatsapp" },
    { icon: Mail, label: c.email, value: company.email, href: `mailto:${company.email}`, testId: "contact-email" },
    { icon: MapPin, label: c.address, value: company.address, testId: "contact-address" },
    { icon: Clock, label: c.hours, value: company.hours, testId: "contact-hours" },
    { icon: Navigation, label: c.area, value: company.areaFull, testId: "contact-area" },
  ];

  return (
    <>
      <Seo
        title={lang === "en" ? "Contact | EMME SERVICE CAR" : "Contatti | EMME SERVICE CAR"}
        description={lang === "en" ? "Call EMME SERVICE CAR, message us on WhatsApp or send your location. Request a scheduled transport quote online." : "Chiama EMME SERVICE CAR, scrivici su WhatsApp o invia la tua posizione. Richiedi un preventivo per trasporto programmato online."}
        path="/contatti"
      />

      <section className="relative bg-carbon pb-12 pt-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-xs text-steel">
            <Link to="/" className="hover:text-amber">{t.breadcrumbHome}</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-ice">{t.nav.contact}</span>
          </nav>
          <Overline>{c.overline}</Overline>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-ice sm:text-5xl">{c.title}</h1>

          {/* Emergency actions first (mobile) */}
          <div className="mt-8 flex flex-wrap gap-3">
            <CallButton size="lg" testId="contact-call-btn" />
            <WhatsAppButton size="lg" testId="contact-whatsapp-btn" />
            <LocationButton size="lg" testId="contact-location-btn" />
          </div>
        </div>
      </section>

      <section className="bg-carbon pb-16">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 lg:grid-cols-3 lg:px-8">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {details.map((d) => {
                const Inner = (
                  <div className="flex items-start gap-4 rounded-xl border border-edge bg-asphalt p-5 transition-colors hover:border-amber">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-edge bg-carbon text-amber">
                      <d.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-steel">{d.label}</p>
                      <p className="mt-1 text-sm font-medium text-ice">{d.value}</p>
                    </div>
                  </div>
                );
                return d.href ? (
                  <a key={d.testId} href={d.href} data-testid={d.testId} target={d.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">{Inner}</a>
                ) : (
                  <div key={d.testId} data-testid={d.testId}>{Inner}</div>
                );
              })}
            </div>
          </div>

          {/* Prepare box */}
          <Reveal>
            <div className="rounded-xl border border-amber/40 bg-amber/5 p-6" data-testid="prepare-box">
              <h3 className="font-display text-lg font-bold text-amber">{c.prepareTitle}</h3>
              <ul className="mt-4 space-y-2.5">
                {c.prepare.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm text-ice">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-amber" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <OperationalArea />
      <QuoteForm />
    </>
  );
}
